<template>
  <div class="login-page">
    <div class="login-card">
      <header class="login-card__header">
          <div class="login-card__logo">
            <img :src="logoSr" alt="Smart Remont" />
          </div>
          <h1 class="login-card__title">Вход в My Space</h1>
          <p class="login-card__subtitle">Введите учётные данные для продолжения</p>
        </header>

        <q-form class="login-card__form" @submit="onSubmit">
          <BackBtn v-if="showCodeInput" @click="onBack" />

          <CustomInput
            class="login-card__field"
            :value="email"
            @change_input="(val) => (email = val)"
            type="email"
            label="Email"
            lazy-rules
            :rules="[(val) => (val && val.length > 0) || 'Введите email']"
          />
          <CustomInput
            class="login-card__field"
            type="password"
            :value="password"
            @change_input="(val) => (password = val)"
            label="Пароль"
            lazy-rules
            :rules="[(val) => (val && val.length > 0) || 'Введите пароль']"
          />

          <div v-if="showCodeInput" class="login-card__code-row">
            <CustomInput
              class="login-card__field login-card__field--code"
              :value="code_key"
              @change_input="(val) => (code_key = val)"
              label="Код подтверждения"
              input-class="login__form_itemInput"
              lazy-rules
              type="number"
              :rules="[(val) => (val && val.length > 0) || 'Введите код']"
            />
            <q-btn
              outline
              color="primary"
              no-caps
              :loading="codeLoading"
              :disable="codeLoading"
              class="login-card__code-btn"
              @click="sendCodeData"
            >
              <q-icon name="fa-solid fa-paper-plane" size="14px" class="q-mr-xs" />
              Получить код
            </q-btn>
          </div>

          <q-btn
            class="login-card__submit"
            label="Войти"
            type="submit"
            color="primary"
            unelevated
            no-caps
            size="lg"
            :disable="loading || btnDisabled"
            :loading="loading"
          />

          <router-link :to="pages.forgetPassword" class="login-card__link">
            Забыли пароль?
          </router-link>
        </q-form>
    </div>
  </div>
</template>

<script>
import { doLogin, sendLoginCode } from "./services";
import { PAGES } from "@/constants";
import CustomInput from "@/components/form/CustomInput";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import BackBtn from "@/components/common/BackBtn";
import { appActionTypes } from "@/services/store/modules/app_module/app_mutation_types";
import { appActions } from "@/services/store/modules/app_module/app_actions";
import logoSr from "@/assets/img/logo_full.svg";

export default {
  name: "Login",
  components: { BackBtn, CustomInput },
  data() {
    return {
      loading: false,
      btnDisabled: false,
      codeLoading: false,
      showCodeInput: false,
      email: "",
      password: "",
      code_key: "",
      logoSr,
    };
  },
  setup() {
    const { showSucceedSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar };
  },
  methods: {
    async onSubmit() {
      const body = { login: this.email, password: this.password };
      if (this.code_key && this.showCodeInput) {
        body.code_key = this.code_key;
      }
      this.loading = true;
      const res = await doLogin(body);
      this.loading = false;
      if (res) {
        if (res.is_device_exist === false) {
          this.showCodeInput = true;
          return;
        }
        this.btnDisabled = true;
        this.$store.commit(appActions.setAuth());
        this.$store.dispatch(appActionTypes.getUserData);
        this.$store.dispatch(appActionTypes.getCities);
        this.$store.dispatch(appActionTypes.getCompanies);
        this.$router.push("/");
      }
    },
    async sendCodeData() {
      const body = {
        login: this.email,
        password: this.password,
      };
      this.codeLoading = true;
      const res = await sendLoginCode(body);
      this.codeLoading = false;
      if (res) this.showSucceedSnackbar("Код был отправлен на вашу почту");
    },
    onBack() {
      this.showCodeInput = false;
    },
  },
  mounted() {
    if (this.isAuth) this.$router.push("/");
  },
  computed: {
    pages() {
      return PAGES;
    },
    isAuth() {
      return this.$store.state.app.auth;
    },
  },
};
</script>

<style scoped lang="scss">
$brand: #262446;
$brand-light: #3d3a6b;
$surface: #ffffff;
$page-bg: #eef0f6;

.login-page {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 24px 20px;
  background: $page-bg;
}

.login-card {
  width: 100%;
  max-width: 480px;
  background: $surface;
  border-radius: 16px;
  padding: 40px 36px 36px;
  box-shadow:
    0 1px 2px rgba($brand, 0.04),
    0 8px 24px rgba($brand, 0.08),
    0 24px 48px rgba($brand, 0.06);
  border: 1px solid rgba($brand, 0.06);

  &__header {
    text-align: center;
    margin-bottom: 32px;
  }

  &__logo {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100px;
    margin-bottom: 24px;

    img {
      max-height: 92px;
      max-width: 269px;
      width: 100%;
      object-fit: contain;
    }
  }

  &__title {
    margin: 0 0 10px;
    font-size: 32px;
    font-weight: 700;
    color: $brand;
    letter-spacing: -0.03em;
    line-height: 1.15;
  }

  &__subtitle {
    margin: 0;
    font-size: 16px;
    color: #6b7280;
    line-height: 1.5;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__field {
    width: 100%;
    margin: 0;

    :deep(.customInput__label) {
      font-size: 14px;
      margin-bottom: 6px;
    }

    :deep(.q-field__control) {
      min-height: 48px;
    }

    :deep(.q-field__native) {
      font-size: 16px;
    }

    &--code {
      flex: 1;
      min-width: 0;
    }
  }

  &__code-row {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    margin-top: 4px;
  }

  &__code-btn {
    flex-shrink: 0;
    height: 48px;
    margin-bottom: 2px;
    font-size: 14px;
  }

  &__submit {
    width: 100%;
    margin-top: 28px;
    min-height: 52px;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.01em;
    border-radius: 10px;
  }

  &__link {
    display: block;
    margin-top: 24px;
    text-align: center;
    font-size: 15px;
    color: $brand;
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: $brand-light;
      text-decoration: underline;
    }
  }
}

@media (max-width: 399px) {
  .login-card {
    padding: 32px 20px 28px;
    border-radius: 12px;

    &__title {
      font-size: 28px;
    }

    &__code-row {
      flex-direction: column;
      align-items: stretch;
    }

    &__code-btn {
      width: 100%;
      margin-bottom: 0;
    }
  }
}
</style>
